import React, { useEffect, useState, useRef, useCallback } from 'react';

const GALLERY_TABS = ['Photos', 'Videos', 'Annual Day', 'Sports', 'Cultural Events', 'Classroom'];

const Gallery = () => {
  const [headerVisible, setHeaderVisible] = useState(false);
  const [tabsVisible, setTabsVisible] = useState(false);
  const [galleryVisible, setGalleryVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('Photos');
  const [galleryData, setGalleryData] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const sectionRef = useRef(null);

  const openModal = (index) => {
    setSelectedIndex(index);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedIndex(null);
  };

  const nextItem = useCallback(() => {
    if (selectedIndex !== null && galleryData[activeTab]) {
      setSelectedIndex((selectedIndex + 1) % galleryData[activeTab].length);
    }
  }, [selectedIndex, galleryData, activeTab]);

  const prevItem = useCallback(() => {
    if (selectedIndex !== null && galleryData[activeTab]) {
      setSelectedIndex((selectedIndex - 1 + galleryData[activeTab].length) % galleryData[activeTab].length);
    }
  }, [selectedIndex, galleryData, activeTab]);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e) => {
      if (!isModalOpen) return;
      if (e.key === 'ArrowRight') nextItem();
      if (e.key === 'ArrowLeft') prevItem();
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isModalOpen, nextItem, prevItem]);

  const fetchGallerySection = useCallback(async (section) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/gallery?section=${section}`);
      const result = await response.json();
      if (response.ok && result.success) {
        setGalleryData(prev => ({ ...prev, [section]: result.data || [] }));
      } else {
        setGalleryData(prev => ({ ...prev, [section]: [] }));
      }
    } catch (err) {
      console.error(err);
      setGalleryData(prev => ({ ...prev, [section]: [] }));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    GALLERY_TABS.forEach(section => fetchGallerySection(section));
  }, [fetchGallerySection]);

  useEffect(() => {
    if (!galleryData[activeTab]) fetchGallerySection(activeTab);
  }, [activeTab, fetchGallerySection, galleryData]);

  useEffect(() => {
    const timer1 = setTimeout(() => setHeaderVisible(true), 100);
    const timer2 = setTimeout(() => setTabsVisible(true), 300);
    const timer3 = setTimeout(() => setGalleryVisible(true), 500);
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // optional scroll animations
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      observer.disconnect();
    };
  }, []);

  return (
    <div className="bg-background-light dark:bg-background-dark font-display text-slate-800 dark:text-slate-200 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className={`text-center mb-12 transition-all duration-1000 ease-out transform ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="inline-flex items-center gap-2 bg-primary/10 dark:bg-primary/20 px-4 py-2 rounded-full mb-6">
              <span className="text-primary text-lg">📸</span>
              <span className="text-primary font-semibold text-sm">Gallery</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white tracking-tight mb-6">Our Gallery</h1>
            <p className="text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto leading-relaxed">
              A glimpse into the life at Anestar Victory School.
            </p>
          </div>

          {/* Tabs */}
          <div className={`mb-12 transition-all duration-1000 ease-out transform ${tabsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex flex-wrap justify-center border-b border-primary/20 dark:border-primary/30">
              {GALLERY_TABS.map((tab, i) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-3 font-semibold transition-all duration-300 transform hover:scale-105 ${activeTab === tab ? 'text-primary border-b-2 border-primary' : 'text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-slate-300'}`}
                  style={{ transitionDelay: `${400 + i * 50}ms` }}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          {/* Gallery Grid */}
          <div className={`transition-all duration-1000 ease-out transform ${galleryVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} ref={sectionRef}>
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-12">
                {Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-square bg-slate-200 dark:bg-slate-700 rounded-xl animate-pulse"></div>)}
              </div>
            ) : galleryData[activeTab] && galleryData[activeTab].length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-12">
                {galleryData[activeTab].map((item, i) => (
                  <div
                    key={item.id}
                    className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-all duration-500 transform hover:-translate-y-2"
                    style={{ transitionDelay: `${600 + i * 50}ms` }}
                  >
                    <div
                      className="relative w-full h-full cursor-pointer"
                      onClick={() => openModal(i)}
                    >
                      <div
                        className="w-full h-full bg-center bg-no-repeat aspect-square bg-cover transition-transform duration-500 group-hover:scale-110"
                        style={{ backgroundImage: `url("${item.image_url}")` }}
                      ></div>
                      <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                        <span className="text-white text-3xl">{activeTab === 'Videos' ? '▶️' : '🔍'}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 mb-12">
                <div className="text-6xl mb-4">{activeTab === 'Videos' ? '🎥' : '📷'}</div>
                <h3 className="text-xl font-semibold text-slate-600 dark:text-slate-400 mb-2">No {activeTab.toLowerCase()} available</h3>
                <p className="text-slate-500 dark:text-slate-500">Check back later for {activeTab.toLowerCase()} content.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Fullscreen Modal */}
      {isModalOpen && selectedIndex !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90">
          <div className="absolute inset-0" onClick={closeModal}></div>

          {/* Navigation Arrows */}
          <button onClick={prevItem} className="absolute left-4 top-1/2 -translate-y-1/2 text-white text-3xl px-2 py-1 hover:bg-black/50 rounded-full">‹</button>
          <button onClick={nextItem} className="absolute right-4 top-1/2 -translate-y-1/2 text-white text-3xl px-2 py-1 hover:bg-black/50 rounded-full">›</button>

          {/* Conditional rendering for images/videos */}
          {activeTab === 'Videos' ? (
            (() => {
              const item = galleryData[activeTab][selectedIndex];
              if (!item.video_url) return null;

              // YouTube embed
              if (item.video_url.includes('youtube.com') || item.video_url.includes('youtu.be')) {
                const embedUrl = item.video_url.includes('watch?v=')
                  ? item.video_url.replace('watch?v=', 'embed/')
                  : item.video_url.replace('youtu.be/', 'youtube.com/embed/');
                return (
                  <iframe
                    src={embedUrl}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="max-w-[95vw] max-h-[95vh] rounded-md shadow-lg"
                    title="YouTube Video"
                  />
                );
              }

              // Uploaded video files
              return (
                <video
                  src={item.video_url}
                  controls
                  autoPlay
                  className="max-w-[95vw] max-h-[95vh] object-contain rounded-md shadow-lg"
                />
              );
            })()
          ) : (
            <img
              src={galleryData[activeTab][selectedIndex].image_url}
              alt={activeTab}
              className="max-w-[95vw] max-h-[95vh] object-contain rounded-md shadow-lg animate-scaleIn"
            />
          )}

          {/* Close button */}
          <button
            onClick={closeModal}
            className="absolute top-4 right-4 p-2 bg-black/50 rounded-full hover:bg-black/70 text-white text-xl"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
};

export default Gallery;
